import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Image as ImageIcon } from 'lucide-react';
import { UtensilsCrossed } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiClient';
import { useNotification } from '../context/NotificationContext';
import './Menu.css';

const Menu = () => {
  const { notifySuccess, notifyError, showLoading, hideLoading } = useNotification();
  const [menuItems, setMenuItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  const [formData, setFormData] = useState({
    name: '', description: '', price: '', category: 'Main Course', sub_category: '', status: 'available'
  });
  const [imageFile, setImageFile] = useState(null);

  const fetchMenu = async () => {
    try {
      const response = await apiFetch('/api/menu');
      if (response.ok) {
        const data = await response.json();
        setMenuItems(data);
      }
    } catch (error) {
      console.error('Error fetching menu');
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('description', formData.description);
    payload.append('price', formData.price);
    payload.append('category', formData.category);
    payload.append('sub_category', formData.sub_category);
    payload.append('status', formData.status);

    if (imageFile) {
      payload.append('image', imageFile);
    }

    showLoading('Saving dish…');
    try {
      const response = await apiFetch('/api/menu', {
        method: 'POST',
        headers: {
        },
        body: payload
      });

      if (response.ok) {
        setIsAdding(false);
        setFormData({ name: '', description: '', price: '', category: 'Main Course', sub_category: '', status: 'available' });
        setImageFile(null);
        fetchMenu();
        notifySuccess('Menu item saved successfully');
      } else {
        notifyError('Failed to save menu item');
      }
    } catch (error) {
      notifyError('Failed to save menu item');
    } finally {
      hideLoading();
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'available' ? 'unavailable' : 'available';
    try {
      const response = await apiFetch(`/api/menu/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchMenu();
      } else {
        notifyError('Failed to update item status');
      }
    } catch (e) {
      notifyError('Failed to update item status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this menu item completely?")) return;
    showLoading('Deleting item…');
    try {
      const response = await apiFetch(`/api/menu/${id}`, { method: 'DELETE' });
      if (response.ok) {
        fetchMenu();
        notifySuccess('Menu item deleted successfully');
      } else {
        notifyError('Failed to delete item');
      }
    } catch (e) {
      notifyError('Failed to delete item');
    } finally {
      hideLoading();
    }
  };

  return (
    <div className="menu-page">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Menu & Catalog</h1>
        <button className="btn btn-primary" onClick={() => setIsAdding(!isAdding)}>
          <Plus size={18} style={{ marginRight: '8px' }} />
          Add Dish
        </button>
      </div>

      {isAdding && (
        <div className="card" style={{ marginBottom: '24px', animation: 'slideUp 0.3s ease-out' }}>
          <h3>Add New Menu Item</h3>
          <form className="form-grid-2col" style={{ marginTop: '16px' }} onSubmit={handleSubmit}>
            <div>
              <label className="label">Dish Name</label>
              <input type="text" name="name" className="input-field" required value={formData.name} onChange={handleInputChange} />
            </div>
            <div>
              <label className="label">Price ($)</label>
              <input type="number" step="0.01" name="price" className="input-field" required value={formData.price} onChange={handleInputChange} />
            </div>
            <div>
              <label className="label">Category</label>
              <select name="category" className="input-field" value={formData.category} onChange={handleInputChange}>
                <option value="Starters">Starters</option>
                <option value="Main Course">Main Course</option>
                <option value="Desserts">Desserts</option>
                <option value="Beverages">Beverages</option>
              </select>
            </div>
            <div>
              <label className="label">Sub Category (Optional)</label>
              <input type="text" name="sub_category" className="input-field" value={formData.sub_category} onChange={handleInputChange} placeholder="e.g. Vegan, Spicy" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="label">Description</label>
              <textarea name="description" className="input-field" rows="2" value={formData.description} onChange={handleInputChange}></textarea>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="label">Dish Image</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input type="file" accept="image/*" onChange={handleFileChange} />
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Files are saved directly to the Node.js server.</span>
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
              <button type="button" className="btn" onClick={() => setIsAdding(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save Dish</button>
            </div>
          </form>
        </div>
      )}

      <div className="menu-cards-grid">
        {menuItems.map(item => (
          <div className="card" key={item.id} style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {item.image_url ? (
              <img src={`${API_BASE_URL}${item.image_url}`} alt={item.name} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '180px', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                <ImageIcon size={48} opacity={0.5} />
              </div>
            )}

            <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600' }}>{item.name}</h3>
                <span style={{ fontWeight: '700', color: 'var(--primary-color)' }}>${item.price}</span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px', flex: 1 }}>{item.description || 'No description provided.'}</p>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                <span style={{ background: 'var(--bg-color)', padding: '4px 8px', borderRadius: '4px', fontWeight: '500' }}>{item.category}</span>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleToggleStatus(item.id, item.status)}
                    style={{
                      background: item.status === 'available' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: item.status === 'available' ? 'var(--success-color)' : 'var(--danger-color)',
                      border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px'
                    }}>
                    {item.status === 'available' ? 'Available' : 'Sold Out'}
                  </button>
                  <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', padding: '4px' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {menuItems.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
            <UtensilsCrossed size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <p>Your menu is empty. Click "Add Dish" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
